import { useMemo, useState } from 'react';
import { useQuery } from '../../hooks/useQuery';
import { useDirtyFlag } from '../../hooks/useDirtyGuard';
import { api, type PlanVersion, type PlanItemRow, type CatalogLineRow, type ItemMethod, type SeedPreview } from '../../lib/api';
import { Spinner, ActionButton, Select, Modal } from '../ui';
import { SaveStatus, useSaveState } from '../SaveStatus';
import CellLineage from './CellLineage';

/**
 * Planning v2 Budget Grid — the primary editing surface. Rows are the catalog
 * line hierarchy; under each leaf sit its plan items (editable). Computed lines
 * (gm, op_profit, cash) render read-only in their catalog positions. Manual item
 * cells edit directly; derived item cells prompt an override. Adding/editing an
 * item opens a centered popup (method + its params) so entry never drops to the
 * bottom of the page. Edits buffer locally and commit in one batch, then
 * recompute.
 */

const MONEY = (n: number | undefined) => (n === undefined ? '' : n.toLocaleString(undefined, { maximumFractionDigits: 0 }));

function monthsOf(startYmd: string, endYmd: string): string[] {
  const s = startYmd.slice(0, 7).split('-').map(Number);
  const e = endYmd.slice(0, 7).split('-').map(Number);
  const out: string[] = [];
  let y = s[0]!, m = s[1]!;
  while (y < e[0]! || (y === e[0]! && m <= e[1]!)) { out.push(`${y}-${String(m).padStart(2, '0')}`); m++; if (m > 12) { m = 1; y++; } }
  return out;
}

type PendingValue = { kind: 'value' | 'override'; itemId: string; month: string; amount: number | null };

export default function PlanBudgetGrid({ plan, canWrite, onChanged }: { plan: PlanVersion; canWrite: boolean; onChanged?: () => void }) {
  const planId = plan.id;
  const months = useMemo(() => monthsOf(plan.horizon_start, plan.horizon_end), [plan.horizon_start, plan.horizon_end]);
  const blend = !!plan.actuals_through;

  // activeOnly. The grid was reading the WHOLE catalog, retired lines
  // included, so retiring a line changed nothing here and the cleanup path
  // appeared to do nothing at all.
  const catalogQ = useQuery(() => api.fpa.catalog(true), [planId]);
  const itemsQ = useQuery(() => api.fpa.plans.items(planId), [planId]);
  const linesQ = useQuery(() => (blend ? api.fpa.plans.blendedLines(planId) : api.fpa.plans.lines(planId)), [planId, blend]);
  const deptsQ = useQuery(() => api.fpa.departments(), [planId]);

  const [dept, setDept] = useState<string>('__all__');
  // Mirroring a real chart of accounts yields a couple of hundred leaves and
  // a plan uses a handful of them, so the default view was ~250 rows of zeros
  // with the few that matter buried. On by default; the count is in the label
  // so nothing is hidden silently.
  const [hideEmpty, setHideEmpty] = useState(true);
  const [pending, setPending] = useState<Record<string, PendingValue>>({});
  // Popup editor: create (line/dept preset, no item) or edit (existing item).
  const [editor, setEditor] = useState<{ line: string; dept: string; item?: PlanItemRow } | null>(null);
  const save = useSaveState();
  // Which calculated cell the user asked to explain.
  const [lineage, setLineage] = useState<{ line: string; month: string } | null>(null);

  // Hooks before the loading return: buffered cells are unsaved work, so the
  // guard has to know about them even while a refetch is in flight.
  const pendingCount = Object.keys(pending).length;
  const dirty = pendingCount > 0;
  useDirtyFlag(dirty && canWrite, 'the budget grid');

  if (catalogQ.isLoading || itemsQ.isLoading || linesQ.isLoading) return <Spinner />;
  const catalog = catalogQ.data?.catalog ?? [];
  const items = itemsQ.data?.items ?? [];
  const lines = (linesQ.data?.lines ?? []) as Array<{ line_code: string; department_id: string | null; period_month: string; amount: number; source?: string }>;
  const departments = deptsQ.data?.departments ?? [];
  const deptName = (id: string) => departments.find((d) => d.id === id)?.name ?? id;

  const scopeDept = dept === '__all__' ? null : dept;
  const lineAt = (code: string, m: string) => lines.find((l) => l.line_code === code && l.department_id === scopeDept && l.period_month === m)?.amount;
  const sourceAt = (code: string, m: string) => lines.find((l) => l.line_code === code && l.department_id === scopeDept && l.period_month === m)?.source;

  const byParent = new Map<string | null, CatalogLineRow[]>();
  for (const c of [...catalog].sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code))) {
    const k = c.parent_code;
    (byParent.get(k) ?? byParent.set(k, []).get(k)!).push(c);
  }
  const itemsByLine = new Map<string, PlanItemRow[]>();
  for (const it of items) {
    if (scopeDept && it.department_id !== scopeDept) continue;
    (itemsByLine.get(it.line_code) ?? itemsByLine.set(it.line_code, []).get(it.line_code)!).push(it);
  }

  // An item whose line_code has no catalog row renders NOWHERE in the tree
  // below, because the tree is driven by the catalog. It still feeds the
  // recompute and moves every subtotal, so dropping it silently is the worst
  // option. QBO account mappings and the fixed-cost backfill can both mint
  // codes (cost.fixed.insurance and the like) that were never registered.
  const catalogCodes = new Set(catalog.map((c) => c.code));
  const unmappedCodes = [...itemsByLine.keys()].filter((c) => !catalogCodes.has(c)).sort();
  const unmappedCount = unmappedCodes.reduce((n, c) => n + (itemsByLine.get(c)?.length ?? 0), 0);
  // Company-level items (department_id null) only appear under Consolidated.
  const companyItemCount = items.filter((it) => it.department_id === null).length;
  // Counted across every department, not just the one in view: re-seeding is
  // plan-wide, so hiding the button while scoped to a department would hide a
  // control that still has work to do.
  const seedCount = items.filter((it) => it.method === 'actuals_seed').length;

  // A line is worth showing when it carries an item or a non-zero figure in
  // this department scope - or when anything beneath it does, so a branch is
  // never cut off from a child that matters.
  const nonZero = new Set<string>();
  for (const l of lines) if (l.amount !== 0 && l.department_id === scopeDept) nonZero.add(l.line_code);
  const keepCache = new Map<string, boolean>();
  function keepNode(code: string): boolean {
    const cached = keepCache.get(code);
    if (cached !== undefined) return cached;
    keepCache.set(code, true);   // guards a malformed parent cycle
    const own = nonZero.has(code) || (itemsByLine.get(code)?.length ?? 0) > 0;
    const kids = byParent.get(code) ?? [];
    const any = own || kids.some((k) => keepNode(k.code));
    keepCache.set(code, any);
    return any;
  }
  // Subtotals and the cash lines always render: a statement that drops gross
  // margin because it happens to be zero is not a statement.
  const alwaysShow = (c: CatalogLineRow) => c.kind === 'computed' || c.family === 'subtotal' || c.family === 'cash';
  const hidden = catalog.filter((c) => c.kind === 'leaf' && !alwaysShow(c) && !keepNode(c.code)).length;

  const bufferKey = (kind: string, itemId: string, m: string) => `${kind}|${itemId}|${m}`;
  function displayVal(it: PlanItemRow, m: string): number | undefined {
    const pv = pending[bufferKey('value', it.id, m)] ?? pending[bufferKey('override', it.id, m)];
    if (pv) return pv.amount ?? undefined;
    if (it.overrides?.[m]) return it.overrides[m]!.amount;
    // Computed series covers every method (manual surfaces its stored values);
    // fall back to raw values for freshly-typed cells before recompute.
    if (it.computed?.[m] !== undefined) return it.computed[m];
    if (it.values?.[m] !== undefined) return it.values[m];
    return undefined;
  }
  function setCell(it: PlanItemRow, m: string, raw: string) {
    const amount = raw.trim() === '' ? null : Number(raw);
    if (amount !== null && !Number.isFinite(amount)) return;
    const kind: 'value' | 'override' = it.method === 'manual' || it.method === 'actuals_seed' ? 'value' : 'override';
    setPending((p) => ({ ...p, [bufferKey(kind, it.id, m)]: { kind, itemId: it.id, month: m, amount } }));
    save.clear();   // editing again retires the last "Saved" line
  }

  // Refresh everything the grid draws from. The CATALOG belongs in here: it
  // decides which rows exist and where an item is drawn, so after a catalog
  // change (registering a custom line code, say) an already-open page kept
  // showing those items in the "not in the line catalog" bucket until
  // somebody reloaded. useQuery only refetches when its deps change, and the
  // catalog query is keyed on planId alone.
  function refreshAll() {
    catalogQ.refetch();
    itemsQ.refetch();
    linesQ.refetch();
    onChanged?.();
  }

  // A plan can need a recompute with nothing buffered - a clone used to open
  // with every input copied and a completely blank statement - so the apply
  // button is never disabled for want of pending edits.
  // WHY THESE ARE ONE BUTTON NOW. A cell typed in the grid lives only in
  // `pending` until a save; /recompute rebuilds plan_lines from the inputs
  // the server has COMMITTED, so it cannot see those cells. Side by side,
  // "Recompute" read as the button that applies what you just typed, and it
  // honestly reported success while changing nothing - the item's own row
  // total moved (it reads the buffer) while every subtotal above it stayed
  // put. One button removes the choice: it saves first when anything is
  // buffered, and is a plain recompute only when there is nothing to save.
  async function runApply() {
    if (dirty) await runSave();
    else await runRecompute();
  }

  // Seeded items are pinned when written, so nothing refreshes them on its
  // own. This is the deliberate act that re-reads every seed from its source
  // - after a QBO sync, or once an account mapping has been corrected.
  async function runReseed() {
    let reseeded = 0;
    let failed: Array<{ label: string; error: string }> = [];
    const ok = await save.run(async () => {
      const out = await api.fpa.plans.reseed(planId);
      reseeded = out.reseeded; failed = out.failed;
    }, 'items re-seeded');
    if (!ok) return;
    refreshAll();
    // A partial run still committed the items it could resolve, so the grid
    // is refreshed before reporting the ones it could not.
    if (failed.length) {
      save.fail(`${reseeded} re-seeded, ${failed.length} could not: `
        + failed.map((f) => `${f.label} — ${f.error}`).join('; '));
    }
  }

  async function runRecompute() {
    const ok = await save.run(() => api.fpa.plans.recompute(planId), 'recomputed');
    if (!ok) return;
    refreshAll();
  }

  async function runSave() {
    const values = Object.values(pending).filter((p) => p.kind === 'value').map((p) => ({ item_id: p.itemId, period_month: p.month, amount: p.amount }));
    const overrides = Object.values(pending).filter((p) => p.kind === 'override').map((p) => ({ item_id: p.itemId, period_month: p.month, amount: p.amount }));
    // The batch recomputes in the same transaction, so there's no separate
    // recompute call. The button is disabled unless something is pending.
    const ok = await save.run(
      () => api.fpa.plans.itemsBatch(planId, { values, overrides }),
      `${values.length + overrides.length} cell${values.length + overrides.length === 1 ? '' : 's'}`,
    );
    // Only drop the buffer once the server has it — a failed save keeps the
    // typed numbers on screen (and the page still counts as dirty).
    if (!ok) return;
    setPending({});
    refreshAll();
  }
  async function deleteItem(id: string) {
    if (!window.confirm('Delete this item and its values/overrides?')) return;
    const ok = await save.run(() => api.fpa.plans.itemsBatch(planId, { deleteItems: [id] }), 'item deleted');
    if (!ok) return;
    refreshAll();
  }

  const cellStyle: React.CSSProperties = { padding: '2px 6px', textAlign: 'right', fontSize: 11, fontVariantNumeric: 'tabular-nums', borderBottom: '0.5px solid var(--color-border)' };
  const labelStyle: React.CSSProperties = { padding: '3px 8px', fontSize: 11, borderBottom: '0.5px solid var(--color-border)', position: 'sticky', left: 0, background: 'var(--color-surface)', zIndex: 1 };
  // Visible affordance so it's obvious the monthly cells are editable.
  const input: React.CSSProperties = { width: 66, fontSize: 11, textAlign: 'right', border: '0.5px solid var(--color-border)', borderRadius: 3, padding: '1px 4px', background: 'var(--color-surface)', color: 'var(--color-text)', fontVariantNumeric: 'tabular-nums' };
  const editableCanWrite = canWrite && plan.status === 'draft';

  // A calculated amount, clickable through to its provenance. Item rows are
  // NOT rendered this way: they are the input, so there is nothing to explain.
  function calcCell(code: string, m: string, bold = false) {
    const v = lineAt(code, m);
    if (v === undefined) return null;
    return (
      <span
        onClick={() => setLineage({ line: code, month: m })}
        title="Where does this come from?"
        style={{ cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 2, fontWeight: bold ? 600 : undefined }}
      >{MONEY(v)}</span>
    );
  }

  function renderLeaf(leaf: Pick<CatalogLineRow, 'code' | 'label'>, indent: number) {
    const its = itemsByLine.get(leaf.code) ?? [];
    const rows: React.ReactNode[] = [];
    rows.push(
      <tr key={`leaf-${leaf.code}`}>
        <td style={{ ...labelStyle, paddingLeft: 8 + indent * 12, fontWeight: 500 }}>
          {leaf.label}
          {editableCanWrite && (
            <button onClick={() => setEditor({ line: leaf.code, dept })} style={{ marginLeft: 6, fontSize: 10, background: 'none', border: 'none', color: 'var(--color-blue)', cursor: 'pointer' }}>+ item</button>
          )}
        </td>
        <td style={cellStyle}>{MONEY(months.reduce((s, m) => s + (lineAt(leaf.code, m) ?? 0), 0))}</td>
        {months.map((m) => {
          const src = sourceAt(leaf.code, m);
          return <td key={m} style={{ ...cellStyle, background: src === 'actual' ? 'var(--color-surface-alt, rgba(120,120,120,0.08))' : undefined }}>{calcCell(leaf.code, m)}</td>;
        })}
      </tr>,
    );
    for (const it of its) {
      const isOverrideRow = it.method !== 'manual' && it.method !== 'actuals_seed';
      rows.push(
        <tr key={`item-${it.id}`} style={{ opacity: it.is_active ? 1 : 0.5 }}>
          <td style={{ ...labelStyle, paddingLeft: 8 + (indent + 1) * 12, color: 'var(--color-text-muted)' }}>
            <button onClick={() => setEditor({ line: it.line_code, dept: it.department_id ?? '__all__', item: it })}
              title="Edit item" style={{ background: 'none', border: 'none', color: 'var(--color-text)', cursor: 'pointer', padding: 0, fontSize: 11, textAlign: 'left' }}>
              {it.label}
            </button>
            <span style={{ fontSize: 9, marginLeft: 4, color: 'var(--color-text-muted)' }}>{it.method}{it.sign === -1 ? ' (−)' : ''}</span>
            {/* Consolidated lists every department's items, and two items on
                the same line often carry the same label - the backfill names
                them all '<line> (backfilled)'. Without the department they
                are indistinguishable, and identical rows read as duplicates. */}
            <span style={{ fontSize: 9, marginLeft: 6, color: 'var(--color-text-muted)' }}>
              {it.department_id ? deptName(it.department_id) : 'company-wide'}
            </span>
          </td>
          <td style={cellStyle}>{MONEY(months.reduce((s, m) => s + (displayVal(it, m) ?? 0), 0))}</td>
          {months.map((m) => {
            const overridden = !!it.overrides?.[m] || !!pending[bufferKey('override', it.id, m)];
            // Typed but not yet sent. The input is uncontrolled, so the text
            // sits in the DOM looking exactly like a saved figure.
            const unsaved = !!pending[bufferKey('value', it.id, m)] || !!pending[bufferKey('override', it.id, m)];
            const editable = editableCanWrite && (!blend || m > (plan.actuals_through ?? ''));
            return (
              <td key={m} style={{ ...cellStyle, background: overridden ? 'rgba(240,180,0,0.14)' : undefined, boxShadow: unsaved ? 'inset 2px 0 0 var(--color-amber)' : undefined }}
                title={unsaved ? 'Unsaved — press Save & recompute to apply it' : overridden ? 'override' : isOverrideRow ? 'derived — type to override' : ''}>
                {editable
                  ? <input style={input} defaultValue={displayVal(it, m) ?? ''} onBlur={(e) => setCell(it, m, e.target.value)} />
                  : MONEY(displayVal(it, m))}
              </td>
            );
          })}
        </tr>,
      );
    }
    return rows;
  }

  function renderNode(code: string | null, indent: number): React.ReactNode[] {
    const children = byParent.get(code) ?? [];
    const out: React.ReactNode[] = [];
    for (const c of children) {
      if (hideEmpty && !alwaysShow(c) && !keepNode(c.code)) continue;
      if (c.kind === 'leaf') {
        out.push(...renderLeaf(c, indent));
        // A leaf is not SUPPOSED to have children, but it can end up with them:
        // the QBO mirror creates a line as a leaf when the account had no
        // children at the time, and a later run finds that it does. Recursing
        // anyway means the children are visible and wrong rather than invisible
        // and wrong — a whole branch silently absent from the statement is the
        // worse failure, and it reads as "the hierarchy didn't import".
        out.push(...renderNode(c.code, indent + 1));
        continue;
      }
      const isComputed = c.kind === 'computed';
      out.push(
        <tr key={`grp-${c.code}`} style={{ background: 'var(--color-surface-alt, rgba(120,120,120,0.04))' }}>
          <td style={{ ...labelStyle, paddingLeft: 8 + indent * 12, fontWeight: 600, background: 'var(--color-surface-alt, rgba(120,120,120,0.04))' }}>{c.label}</td>
          <td style={{ ...cellStyle, fontWeight: 600 }}>{MONEY(months.reduce((s, m) => s + (lineAt(c.code, m) ?? 0), 0))}</td>
          {months.map((m) => <td key={m} style={{ ...cellStyle, fontWeight: 600 }}>{calcCell(c.code, m, true)}</td>)}
        </tr>,
      );
      if (!isComputed) out.push(...renderNode(c.code, indent + 1));
    }
    return out;
  }

  function renderUnmapped(): React.ReactNode[] {
    if (!unmappedCodes.length) return [];
    const out: React.ReactNode[] = [
      <tr key="unmapped-head" style={{ background: 'rgba(240,180,0,0.14)' }}>
        <td colSpan={months.length + 2} style={{ ...labelStyle, fontWeight: 600, background: 'rgba(240,180,0,0.14)' }}>
          Not in the line catalog — {unmappedCount} item{unmappedCount === 1 ? '' : 's'}
          <span style={{ fontWeight: 400, fontSize: 10, color: 'var(--color-text-muted)', marginLeft: 6 }}>
            these already count toward the totals above, but their line has no place in the statement
          </span>
        </td>
      </tr>,
    ];
    for (const code of unmappedCodes) out.push(...renderLeaf({ code, label: code }, 0));
    return out;
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 12px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Department</span>
        <Select value={dept} onChange={setDept}>
          <option value="__all__">Consolidated</option>
          {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </Select>
        {editableCanWrite && <ActionButton onClick={() => setEditor({ line: 'cost.fixed.rent', dept })}>+ Add item</ActionButton>}
        <label style={{ fontSize: 10, color: 'var(--color-text-muted)', display: 'flex', gap: 4, alignItems: 'center' }}
          title="Hide lines with no budget item and no figures in this scope. A line is kept if anything beneath it has either.">
          <input type="checkbox" checked={hideEmpty} onChange={(e) => setHideEmpty(e.target.checked)} />
          hide empty{hidden > 0 ? ` (${hidden})` : ''}
        </label>
        {blend && <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>Actuals through {plan.actuals_through} (grey = actual)</span>}
        {scopeDept && companyItemCount > 0 && (
          <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>
            {companyItemCount} company-level item{companyItemCount === 1 ? '' : 's'} not shown here — pick Consolidated
          </span>
        )}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <SaveStatus state={save} />
          {dirty && save.stage !== 'saving' && (
            <span style={{ fontSize: 11, color: 'var(--color-amber)' }}
              title="Subtotals and summary lines are calculated by the server from saved inputs, so they do not include these cells yet.">
              {pendingCount} unsaved cell{pendingCount === 1 ? '' : 's'} — subtotals exclude {pendingCount === 1 ? 'it' : 'them'}
            </span>
          )}
          {editableCanWrite && seedCount > 0 && (
            <ActionButton onClick={runReseed} disabled={save.busy}
              title="Re-read every seeded item from QBO actuals or its source plan. Seeded figures are pinned when saved, so they do not move on their own.">
              Re-seed ({seedCount})
            </ActionButton>
          )}
          {editableCanWrite && (
            <ActionButton variant="primary" onClick={runApply} disabled={save.busy}>
              {save.busy ? 'Saving…' : dirty ? `Save & recompute (${pendingCount})` : 'Recompute'}
            </ActionButton>
          )}
        </span>
      </div>
      {lines.length === 0 && (
        <div style={{ margin: '0 12px 8px', padding: '8px 10px', background: 'rgba(240,180,0,0.14)', borderRadius: 'var(--radius-sm)', fontSize: 11 }}>
          This plan has no calculated lines yet, so every cell below is blank. Its inputs may still be
          there — press Recompute to build the statement from them.
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
          <thead>
            <tr>
              <th style={{ ...labelStyle, fontWeight: 600, textAlign: 'left', zIndex: 2 }}>Line</th>
              <th style={{ ...cellStyle, fontWeight: 600 }}>Total</th>
              {months.map((m) => <th key={m} style={{ ...cellStyle, fontWeight: 600 }}>{m.slice(2)}</th>)}
            </tr>
          </thead>
          <tbody>{renderNode(null, 0)}{renderUnmapped()}</tbody>
        </table>
      </div>
      {lineage && (
        <CellLineage
          planId={planId} line={lineage.line} dept={scopeDept} month={lineage.month}
          canOverride={editableCanWrite}
          onClose={() => setLineage(null)}
          onChanged={() => { refreshAll(); }}
          onOpenItem={(itemId) => {
            const it = items.find((x) => x.id === itemId);
            if (it) setEditor({ line: it.line_code, dept: it.department_id ?? '__all__', item: it });
          }}
        />
      )}
      {editor && (
        <ItemEditor
          planId={planId} line={editor.line} presetDept={editor.dept} item={editor.item} months={months}
          catalog={catalog} departments={departments}
          onClose={() => setEditor(null)}
          onSaved={(note) => { setEditor(null); save.markSaved(note); refreshAll(); }}
          onDelete={editor.item ? () => { const id = editor.item!.id; setEditor(null); deleteItem(id); } : undefined}
        />
      )}
    </div>
  );
}

// ─── Item editor popup (create + edit, with method-specific params) ──────────

const METHODS: ItemMethod[] = ['manual', 'spread', 'growth', 'pct_of', 'per_head', 'formula', 'actuals_seed'];
// The raw ids are the stored values and the API contract; these are what an
// operator reads. 'actuals_seed' in particular told nobody what it did.
const METHOD_LABELS: Record<string, string> = {
  manual: 'Manual — type the numbers',
  spread: 'Spread a total over a period',
  growth: 'Grow from a base',
  pct_of: 'Percent of another line',
  per_head: 'Per head of headcount',
  formula: 'Formula',
  actuals_seed: 'Copy from actuals or another plan',
};

// Percentages are entered as whole numbers (10 = 10%) and stored as decimals.
/** Month arithmetic on 'YYYY-MM', for the seed window defaults. */
function shiftMonth(month: string, byMonths: number): string {
  if (!/^\d{4}-\d{2}$/.test(month)) return '';
  const [y, m] = month.split('-').map(Number) as [number, number];
  const total = y * 12 + (m - 1) + byMonths;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}

function defaultParams(method: ItemMethod, line: string, months: string[] = []): Record<string, unknown> {
  switch (method) {
    case 'spread': return { total: 0, period: 'year', profile: 'even' };
    case 'growth': return { base: 0, rate: 0, cadence: 'mom' };
    case 'pct_of': return { ref: 'revenue.total', pct: 0, lag_months: 0, scope: 'same_department' };
    case 'per_head': return { amount_per_head_month: 0, headcount_filter: {} };
    case 'formula': return { expression: '0' };
    // A 12-month window ending just before the horizon, which is the common
    // case (last full year -> next year) and gives Preview something to show
    // immediately. `months` is the plan horizon, passed in by the caller.
    case 'actuals_seed': return {
      source: 'qbo_actuals', source_line: line, source_department: 'same',
      from: shiftMonth(months[0] ?? '', -12), to: shiftMonth(months[0] ?? '', -1),
      mode: 'average', uplift_pct: 0,
    };
    default: return {};
  }
}

// Shared field chrome, out here so <NumField> can use it too.
const fieldBox: React.CSSProperties = { fontSize: 12, padding: '4px 7px', border: '0.5px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)' };

/**
 * Uncontrolled numeric field (defaultValue + onChange) so a mid-typing value
 * like "0.05" or "5" is never reformatted out from under the cursor.
 *
 * WHY IT LIVES OUT HERE. Declared inside ItemEditor, this was a different
 * component TYPE on every render: each keystroke called setParams, ItemEditor
 * re-rendered, React found an unfamiliar type in that slot and so unmounted
 * the live input to mount a fresh one - the field lost focus after every
 * single character. A stable module-level type reconciles as the same
 * element and the input is left alone. Never declare a component inside a
 * component that re-renders while it holds focus.
 */
function NumField({ value, onChange, width = 140, pct = false }: {
  value: unknown; onChange: (n: number) => void; width?: number; pct?: boolean;
}) {
  const num = (v: string) => (v.trim() === '' ? 0 : Number(v));
  return (
    <input type="number" step="any"
      defaultValue={pct ? String(Number(value ?? 0) * 100) : String(Number(value ?? 0))}
      onChange={(e) => onChange(pct ? num(e.target.value) / 100 : num(e.target.value))}
      style={{ ...fieldBox, width }} />
  );
}

function ItemEditor({ planId, line, presetDept, item, months, catalog, departments, onClose, onSaved, onDelete }: {
  planId: string; line: string; presetDept: string; item?: PlanItemRow; months: string[];
  catalog: CatalogLineRow[]; departments: Array<{ id: string; name: string }>;
  onClose: () => void; onSaved: (note: string) => void; onDelete?: () => void;
}) {
  const leaves = catalog.filter((c) => c.kind === 'leaf' && c.is_active);
  const [lineCode, setLineCode] = useState(item?.line_code ?? line);
  const [label, setLabel] = useState(item?.label ?? '');
  const [dept, setDept] = useState(item?.department_id ?? (presetDept === '__all__' ? '__all__' : presetDept));
  const [method, setMethod] = useState<ItemMethod>(item?.method ?? 'manual');
  const [sign, setSign] = useState<1 | -1>(item?.sign ?? 1);
  const [start, setStart] = useState(item?.start_month ?? '');
  const [end, setEnd] = useState(item?.end_month ?? '');
  const [params, setParams] = useState<Record<string, unknown>>(item?.params ?? defaultParams(method, line, months));
  const [fill, setFill] = useState('');           // manual: fill all months with this
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function changeMethod(m: ItemMethod) { setMethod(m); setParams(defaultParams(m, lineCode, months)); }
  const P = (k: string, v: unknown) => setParams((p) => ({ ...p, [k]: v }));
  const g = (k: string) => (params as Record<string, unknown>)[k];

  async function save() {
    if (!label.trim()) { setErr('Label is required'); return; }
    setBusy(true); setErr(null);
    try {
      const upsert = {
        id: item?.id, line_code: lineCode, label: label.trim(),
        department_id: dept === '__all__' ? null : dept, method, params, sign,
        start_month: start || null, end_month: end || null,
        expected_updated_at: item?.updated_at,
      };
      // Manual quick-fill: the same amount in every horizon month.
      const fillAmt = (method === 'manual' || method === 'actuals_seed') && fill.trim() !== '' ? Number(fill) : NaN;
      const fillValues = (id: string) => months.map((m) => ({ item_id: id, period_month: m, amount: fillAmt }));
      if (item?.id && Number.isFinite(fillAmt)) {
        // Existing item: the upsert and its quick-fill ride one transaction.
        await api.fpa.plans.itemsBatch(planId, { items: [upsert], values: fillValues(item.id) });
      } else {
        const resp = await api.fpa.plans.itemsBatch(planId, { items: [upsert] });
        const itemId = item?.id ?? resp.item_ids[0];
        // A new item's id exists only after the insert, so its quick-fill is a
        // second batch. Each batch recomputes, so both states are consistent.
        if (Number.isFinite(fillAmt) && itemId) await api.fpa.plans.itemsBatch(planId, { values: fillValues(itemId) });
      }
      onSaved(item ? 'item updated' : 'item added');
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  const box: React.CSSProperties = fieldBox;
  const row: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 };
  const lab: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-muted)', width: 110 };
  const muted: React.CSSProperties = { fontSize: 10, color: 'var(--color-text-muted)' };
  // Bound to one params key, so a call site reads the same as it did.
  const numField = (k: string, width?: number, pct?: boolean) => (
    <NumField value={g(k)} onChange={(n) => P(k, n)} width={width} pct={pct} />
  );

  return (
    <Modal title={item ? 'Edit item' : 'Add item'} subtitle={item ? `${item.method} · ${item.line_code}` : lineCode} onClose={onClose} width={520}>
      <div style={{ padding: 20 }}>
        {err && <p style={{ color: 'var(--color-danger, #c0392b)', fontSize: 12 }}>{err}</p>}
        <div style={row}><span style={lab}>Label</span><input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. AWS hosting" style={{ ...box, flex: 1 }} /></div>
        <div style={row}>
          <span style={lab}>Line</span>
          <select value={lineCode} onChange={(e) => setLineCode(e.target.value)} style={{ ...box, flex: 1 }}>
            {leaves.map((c) => <option key={c.code} value={c.code}>{c.label} ({c.code})</option>)}
          </select>
        </div>
        <div style={row}>
          <span style={lab}>Department</span>
          <select value={dept} onChange={(e) => setDept(e.target.value)} style={{ ...box, flex: 1 }}>
            <option value="__all__">Company</option>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div style={row}>
          <span style={lab}>Method</span>
          <select value={method} onChange={(e) => changeMethod(e.target.value as ItemMethod)} style={{ ...box, flex: 1 }} disabled={!!item && item.method.startsWith('engine')}>
            {METHODS.map((m) => <option key={m} value={m}>{METHOD_LABELS[m] ?? m}</option>)}
          </select>
        </div>

        {/* Method-specific params — keyed by method so fields remount cleanly. */}
        <div key={method}>
          {method === 'manual' && (
            <>
              <div style={row}><span style={lab}>Amount / month</span><input type="number" step="any" value={fill} onChange={(e) => setFill(e.target.value)} placeholder="optional — fills every month" style={{ ...box, width: 180 }} /></div>
              {/* Left blank, the item is created with no values at all: it
                  appears in the grid and contributes nothing, which reads as
                  a broken save. Say so here. */}
              <p style={{ ...muted, margin: '-4px 0 10px 118px' }}>
                Leave blank and the item starts at zero — type amounts into its row in the grid,
                then press Save &amp; recompute to apply them.
              </p>
            </>
          )}
          {method === 'spread' && (
            <>
              <div style={row}><span style={lab}>Total</span>{numField('total')}</div>
              <div style={row}><span style={lab}>Period</span><select value={String(g('period') ?? 'year')} onChange={(e) => P('period', e.target.value)} style={box}>{['year', 'quarter', 'horizon'].map((x) => <option key={x}>{x}</option>)}</select>
                <select value={String(g('profile') ?? 'even')} onChange={(e) => P('profile', e.target.value)} style={box}>{['even', 'seasonal', 'custom'].map((x) => <option key={x}>{x}</option>)}</select></div>
            </>
          )}
          {method === 'growth' && (
            <>
              <div style={row}><span style={lab}>Base</span>{numField('base')}</div>
              <div style={row}><span style={lab}>Growth rate</span>{numField('rate', 90, true)}<span style={muted}>% per step</span></div>
              <div style={row}><span style={lab}>Cadence</span><select value={String(g('cadence') ?? 'mom')} onChange={(e) => P('cadence', e.target.value)} style={box}>{['mom', 'yoy', 'annual_step'].map((x) => <option key={x}>{x}</option>)}</select></div>
            </>
          )}
          {method === 'pct_of' && (
            <>
              <div style={row}><span style={lab}>Ref</span><input value={String(g('ref') ?? '')} onChange={(e) => P('ref', e.target.value)} placeholder="revenue.total / rev.tm / …" style={{ ...box, flex: 1 }} /></div>
              <div style={row}><span style={lab}>Percent</span>{numField('pct', 90, true)}<span style={muted}>%</span></div>
              <div style={row}><span style={lab}>Lag (months)</span>{numField('lag_months', 70)}
                <select value={String(g('scope') ?? 'same_department')} onChange={(e) => P('scope', e.target.value)} style={box}>{['same_department', 'company'].map((x) => <option key={x}>{x}</option>)}</select></div>
            </>
          )}
          {method === 'per_head' && (
            <>
              <div style={row}><span style={lab}>$ / head / mo</span>{numField('amount_per_head_month', 120)}</div>
              <div style={row}><span style={lab}>Billable only</span><input type="checkbox" checked={!!(g('headcount_filter') as { billable_only?: boolean } | undefined)?.billable_only} onChange={(e) => P('headcount_filter', { ...(g('headcount_filter') as object), billable_only: e.target.checked })} /></div>
            </>
          )}
          {method === 'formula' && (
            <div style={row}><span style={lab}>Expression</span><input value={String(g('expression') ?? '')} onChange={(e) => P('expression', e.target.value)} placeholder="revenue.total * 0.12" style={{ ...box, flex: 1 }} /></div>
          )}
          {method === 'actuals_seed' && (
            <SeedPanel planId={planId} set={P} get={g} leaves={leaves}
              departments={departments} itemDept={dept === '__all__' ? null : dept}
              lineCode={lineCode} numField={numField} />
          )}
        </div>

        <div style={{ ...row, marginTop: 6 }}>
          <span style={lab}>Sign</span>
          <select value={String(sign)} onChange={(e) => setSign(Number(e.target.value) as 1 | -1)} style={box}><option value="1">+ (normal)</option><option value="-1">− (credit)</option></select>
          <span style={{ ...lab, width: 'auto', marginLeft: 8 }}>Window</span>
          <input value={start} onChange={(e) => setStart(e.target.value)} placeholder="start YYYY-MM" style={{ ...box, width: 110 }} />
          <input value={end} onChange={(e) => setEnd(e.target.value)} placeholder="end YYYY-MM" style={{ ...box, width: 110 }} />
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
          <ActionButton variant="primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : item ? 'Save' : 'Add item'}</ActionButton>
          <ActionButton onClick={onClose}>Cancel</ActionButton>
          {onDelete && <span style={{ marginLeft: 'auto' }}><ActionButton variant="danger" onClick={onDelete}>Delete</ActionButton></span>}
        </div>
      </div>
    </Modal>
  );
}

// ——— Seed an item from history ———————————————————————————————————————————————

/**
 * The actuals_seed editor: where to read, which months, and how that range
 * maps onto this plan's horizon.
 *
 * WHY PREVIEW IS PROMINENT. A seed is resolved on the server and pinned into
 * the item, so without a dry run the first sight of the figures is after a
 * save and a recompute - and if the range or the mode was wrong, the plan has
 * already moved. Preview calls the same resolver the save calls, so what it
 * shows is what a save would store, not an approximation of it.
 *
 * The two ambiguous projections are offered as choices rather than decided
 * here: both readings are things a budget legitimately wants.
 */
function SeedPanel({ planId, set, get, leaves, departments, itemDept, lineCode, numField }: {
  planId: string;
  set: (k: string, v: unknown) => void;
  get: (k: string) => unknown;
  leaves: CatalogLineRow[];
  departments: Array<{ id: string; name: string }>;
  itemDept: string | null;
  lineCode: string;
  numField: (k: string, width?: number, pct?: boolean) => React.ReactNode;
}) {
  const plansQ = useQuery(() => api.fpa.plans.list(), []);
  const [preview, setPreview] = useState<SeedPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const source = String(get('source') ?? 'qbo_actuals');
  const mode = String(get('mode') ?? 'average');
  const s = (k: string, fallback = '') => String(get(k) ?? fallback);
  // Seeding from the plan being edited would make the item an input to its
  // own calculation, so the server refuses it; don't offer it either.
  const otherPlans = (plansQ.data?.plans ?? []).filter((p) => p.id !== planId);

  async function runPreview() {
    setBusy(true); setErr(null);
    try {
      const r = await api.fpa.plans.seedPreview(planId, {
        source: source as 'qbo_actuals' | 'plan',
        source_plan_id: s('source_plan_id') || null,
        source_line: s('source_line') || lineCode,
        source_department: s('source_department', 'same'),
        from: s('from'), to: s('to'),
        mode: mode as 'average' | 'sum' | 'copy',
        sum_distribution: s('sum_distribution', 'even') as 'even' | 'first_month',
        copy_alignment: s('copy_alignment', 'calendar') as 'calendar' | 'position' | 'shift_years',
        copy_shift_years: Number(get('copy_shift_years') ?? 1),
        uplift_pct: Number(get('uplift_pct') ?? 0),
        department_id: itemDept, line_code: lineCode,
      });
      setPreview(r.preview);
    } catch (e) { setErr((e as Error).message); setPreview(null); }
    finally { setBusy(false); }
  }

  const box: React.CSSProperties = { ...fieldBox };
  const row: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 };
  const lab: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-muted)', width: 110 };
  const muted: React.CSSProperties = { fontSize: 10, color: 'var(--color-text-muted)' };
  const money = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });

  return (
    <>
      <div style={row}>
        <span style={lab}>Read from</span>
        <select value={source} onChange={(e) => set('source', e.target.value)} style={box}>
          <option value="qbo_actuals">QBO actuals</option>
          <option value="plan">Another plan</option>
        </select>
        {source === 'plan' && (
          <select value={s('source_plan_id')} onChange={(e) => set('source_plan_id', e.target.value)} style={{ ...box, flex: 1 }}>
            <option value="">— pick a plan —</option>
            {otherPlans.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.status})</option>)}
          </select>
        )}
      </div>

      <div style={row}>
        <span style={lab}>Source line</span>
        <select value={s('source_line') || lineCode} onChange={(e) => set('source_line', e.target.value)} style={{ ...box, flex: 1 }}>
          {leaves.map((c) => <option key={c.code} value={c.code}>{c.label} ({c.code})</option>)}
        </select>
      </div>

      <div style={row}>
        <span style={lab}>Source dept</span>
        <select value={s('source_department', 'same')} onChange={(e) => set('source_department', e.target.value)} style={{ ...box, flex: 1 }}>
          <option value="same">Same as this item{itemDept ? '' : ' (company-wide)'}</option>
          <option value="company">Whole company</option>
          {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>

      <div style={row}>
        <span style={lab}>Months</span>
        <input value={s('from')} onChange={(e) => set('from', e.target.value.trim())} placeholder="2026-01" style={{ ...box, width: 90 }} />
        <span style={muted}>to</span>
        <input value={s('to')} onChange={(e) => set('to', e.target.value.trim())} placeholder="2026-12" style={{ ...box, width: 90 }} />
      </div>

      <div style={row}>
        <span style={lab}>Apply as</span>
        <select value={mode} onChange={(e) => set('mode', e.target.value)} style={box}>
          <option value="average">Average per month</option>
          <option value="sum">Total of the range</option>
          <option value="copy">Copy month by month</option>
        </select>
        {mode === 'sum' && (
          <select value={s('sum_distribution', 'even')} onChange={(e) => set('sum_distribution', e.target.value)} style={box}>
            <option value="even">spread evenly</option>
            <option value="first_month">all in month 1</option>
          </select>
        )}
        {mode === 'copy' && (
          <>
            <select value={s('copy_alignment', 'calendar')} onChange={(e) => set('copy_alignment', e.target.value)} style={box}>
              <option value="calendar">by calendar month</option>
              <option value="position">by position</option>
              <option value="shift_years">shift whole years</option>
            </select>
            {s('copy_alignment', 'calendar') === 'shift_years' && numField('copy_shift_years', 60)}
          </>
        )}
      </div>
      <p style={{ ...muted, margin: '-6px 0 10px 118px' }}>
        {mode === 'average' ? "The range's monthly average, in every month of the horizon."
          : mode === 'sum' ? (s('sum_distribution', 'even') === 'first_month'
            ? "The range's total, all of it in the first horizon month."
            : "The range's total becomes the horizon's total, levelled across its months.")
          : s('copy_alignment', 'calendar') === 'position'
            ? 'First source month onto the first horizon month, in order. Uncovered months stay at zero.'
            : s('copy_alignment', 'calendar') === 'shift_years'
              ? 'Each source month moves forward by whole years. Only months inside the range above count.'
              : 'Jan onto Jan, Feb onto Feb. Months the range does not cover stay at zero; a calendar month appearing twice is averaged.'}
      </p>

      <div style={row}><span style={lab}>Uplift</span>{numField('uplift_pct', 90, true)}<span style={muted}>% applied after</span></div>

      <div style={row}>
        <span style={lab} />
        <ActionButton onClick={runPreview} disabled={busy}>{busy ? 'Reading…' : 'Preview'}</ActionButton>
        <span style={muted}>reads the source without saving anything</span>
      </div>

      {err && <p style={{ fontSize: 11, color: 'var(--color-danger, #c0392b)', marginLeft: 118 }}>{err}</p>}

      {preview && (
        <div style={{ marginLeft: 118, marginBottom: 10, fontSize: 11 }}>
          <p style={{ ...muted, margin: '0 0 4px' }}>{preview.note}</p>
          {preview.source_total === 0 && (
            <p style={{ fontSize: 11, color: 'var(--color-amber)', margin: '0 0 4px' }}>
              The source has nothing on that line for those months — check the line, the department and the range.
            </p>
          )}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <span>Source {money(preview.source_total)} over {preview.range.length} mo</span>
            <span>→ Horizon {money(preview.horizon_total)}</span>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
            {Object.entries(preview.resolved).map(([m, v]) => (
              <span key={m} style={{ fontVariantNumeric: 'tabular-nums' }}>
                <span style={muted}>{m.slice(2)}</span> {money(v)}
              </span>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
